import { useState } from 'react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { INSTALL_COMMANDS, IMPORT_SNIPPETS } from '@/constants/integration';
import { Reveal } from './Reveal';
import { STEPS } from './content';

type Lang = 'node' | 'python';

const LANGS: { id: Lang; label: string }[] = [
  { id: 'node', label: 'Node.js' },
  { id: 'python', label: 'Python' },
];

export function HowItWorks() {
  const [lang, setLang] = useState<Lang>('node');

  const snippetFor = (key?: 'install' | 'import') => {
    if (key === 'install') return INSTALL_COMMANDS[lang];
    if (key === 'import') return IMPORT_SNIPPETS[lang];
    return undefined;
  };

  return (
    <section id="how" className="scroll-mt-16 border-b border-hairline py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 text-center">
          <Badge variant="outline" className="mb-4">
            How it works
          </Badge>
          <h2 className="text-balance text-3xl font-semibold tracking-tight sm:text-4xl">
            Live in under five minutes
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-balance text-base text-muted-foreground">
            No SDK rewrites. No per-call changes. Add one import and watch the
            numbers move.
          </p>
        </div>

        {/* Language toggle — Python and Node are first-class, same one-liner */}
        <div className="mb-10 flex justify-center">
          <div
            className="inline-flex rounded-lg border border-hairline bg-card/40 p-1 text-sm"
            role="tablist"
            aria-label="Choose your language"
          >
            {LANGS.map((l) => (
              <button
                key={l.id}
                type="button"
                role="tab"
                aria-selected={lang === l.id}
                onClick={() => setLang(l.id)}
                className={cn(
                  'rounded-md px-4 py-1.5 font-medium transition-colors duration-150 ease-out-quart',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  lang === l.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {STEPS.map((step, i) => {
            const snippet = snippetFor(step.snippetKey);
            return (
              <Reveal key={step.num} delay={i * 0.08} className="h-full">
                <div className="glass-card flex h-full flex-col gap-4 rounded-xl p-6">
                  <span className="font-mono text-xs font-medium tracking-[0.2em] text-primary">
                    {step.num}
                  </span>
                  <div className="space-y-1.5">
                    <h3 className="text-base font-semibold tracking-tight">
                      {step.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">{step.copy}</p>
                  </div>
                  {snippet && (
                    <pre className="mt-auto overflow-x-auto rounded-lg border border-hairline bg-background/60 px-4 py-3 font-mono text-xs leading-relaxed text-foreground">
                      <code>{snippet}</code>
                    </pre>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
